import {Button} from '@dropbox/dig-components/buttons';
import {FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {LabelGroup} from '@dropbox/dig-components/label_group';
import {Modal} from '@dropbox/dig-components/modal';
import {type OverlayAnchorRef, type OverlayVirtualElement} from '@dropbox/dig-components/overlay';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Select} from '@dropbox/dig-components/text_fields';
import {Tooltip, TooltipTriggerProps} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {WarningLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {CoachSubmissionContext} from 'client';
import {Workstream, WorkstreamMetric} from 'client';
import {insertTitle} from 'components/DSYS/editor/utils/lexical';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {getStatusOptions, StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {Title} from 'components/DSYS/Title';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {isWorkstreamPlanningWindowActive} from 'helpers/workstreamDraftPolicy';
import {useConfirmUnsavedChanges} from 'hooks/useConfirmUnsavedChanges';
import {useWorkstreamDraftPolicy} from 'hooks/useWorkstreamDraftPolicy';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {EditorState} from 'lexical';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {flushSync} from 'react-dom';
import {trackSprigEvent} from 'survey/sprig';
import {useDefaultWorkstreamTimeframe, useWorkstreamAudit} from 'views/workstreams/detail/hooks';
import {workstreamAuditIncludesWorkstream} from 'views/workstreams/detail/workstreamAuditUtils';
import {
  RedesignMetricsList,
  RedesignOutcomesList,
  type WorkstreamMetricUpdate,
} from 'views/workstreams/detail/WorkstreamRedesignSections';

import {
  useWorkstreamDisplayStatus,
  useWorkstreamMetricMutation,
  useWorkstreamStatus,
} from './hooks';
import {useWorkstreamAutodraft} from './useWorkstreamAutodraft';
import {
  getUserType,
  getWorkstreamDri,
  getWorkstreamUpdateVisualStatus,
  isFullWorkstreamMetric,
  metricHasCurrentTargetOrBudget,
} from './utils';
import {
  type ChatDraftHistoryControl,
  type ChatDraftSnapshot,
  MAX_CHAT_DRAFT_UNDO_STACK,
  serializedLexicalJsonIsVisuallyEmpty,
} from './workstreamChatDraftHistory';
import {
  type HealthScoreCounts,
  isTop50OrSubWorkstream,
  useBlockOnTrackForReadiness,
  useWorkstreamHealthScore,
  useWorkstreamHealthScoreCounts,
} from './workstreamChecks';
import {WorkstreamHealthScore} from './WorkstreamHealthScore';
import {getPreviousQuarterPeriod, metricNeedsActualForPeriod} from './workstreamMetricActual';
import styles from './WorkstreamUpdate.module.css';
import {WorkstreamUpdateTabs} from './WorkstreamUpdateTabs';

/** Lexical `EditorState#toString()` is `Object`'s default (`"[object Object]"`); use `toJSON()` for checks and payloads. */
const editorStateHasVisibleContent = (editorState?: EditorState): boolean => {
  if (!editorState) return false;
  return !serializedLexicalJsonIsVisuallyEmpty(JSON.stringify(editorState.toJSON()));
};

type WorkstreamUpdateStep = 'status' | 'metrics' | 'milestones';

const previousQuarterPeriod = getPreviousQuarterPeriod(CURRENT_YEAR, CURRENT_QUARTER);

const needsPreviousQuarterActual = (metric: WorkstreamMetric) =>
  isFullWorkstreamMetric(metric) &&
  metric.type === 'metric' &&
  metricNeedsActualForPeriod(metric, previousQuarterPeriod);

// Prompt for current values and for missing actuals from the previous quarter.
const isPromptableUpdateMetric = (metric: WorkstreamMetric) =>
  (isFullWorkstreamMetric(metric) &&
    metric.type === 'metric' &&
    metricHasCurrentTargetOrBudget(metric)) ||
  needsPreviousQuarterActual(metric);

// Confidential milestones are hidden; readable ones match the modal's milestone list filter.
const isUpdatableMilestone = (metric: WorkstreamMetric) =>
  isFullWorkstreamMetric(metric) && (metric.type === 'milestone' || metric.type === 'deliverable');

const getWorkstreamUpdateSteps = (metrics: WorkstreamMetric[]): WorkstreamUpdateStep[] => [
  'status',
  ...(metrics.some(isPromptableUpdateMetric) ? (['metrics'] as const) : []),
  ...(metrics.some(isUpdatableMilestone) ? (['milestones'] as const) : []),
];

const combineEditorStates = (
  status: string,
  editorStateUpdate?: EditorState,
  editorStateSecondary?: EditorState
): string => {
  const hasUpdateContent = editorStateHasVisibleContent(editorStateUpdate);
  const hasSecondaryContent = editorStateHasVisibleContent(editorStateSecondary);
  const isArchivedStatus = status === 'archived';

  if (!hasUpdateContent && !hasSecondaryContent) return '';
  if (!hasUpdateContent && !isArchivedStatus) return '';

  const updateJson = editorStateUpdate?.toJSON();

  // Standard (non-archived) updates are stored verbatim: a single blob with no injected section
  // titles/prefixes and no `root.meta.type`. Existing updates that already contain
  // "Blockers:" / "Friction:" prefixes still render and edit fine as one blob.
  if (!isArchivedStatus) {
    return updateJson ? JSON.stringify(updateJson) : '';
  }

  // Archived status keeps its dedicated "Relevant workstream" section.
  const secondaryJson = editorStateSecondary?.toJSON();
  const secondaryChildren = hasSecondaryContent
    ? insertTitle(secondaryJson, t('relevant_workstream'), false)
    : [];
  const updateChildren = hasUpdateContent ? insertTitle(updateJson ?? '', t('update'), true) : [];

  return JSON.stringify({
    root: {
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
      meta: {
        type: 'archived',
      },
      children: [...secondaryChildren, ...updateChildren],
    },
  });
};

const BlockedOnTrackStatusWarning = () => (
  <Box
    data-testid="workstream-update-on-track-readiness-tooltip-trigger"
    display="inline-flex"
    alignItems="center"
  >
    <UIIcon src={WarningLine} className={atoms({color: 'Text Base'})} />
  </Box>
);

const getBlockedOnTrackStatusTextElement = (element: HTMLElement) =>
  element.querySelector<HTMLElement>('.dig-Select-trigger-label, .dig-Menu-row-title') ?? element;

const getBlockedOnTrackStatusTextAnchor = (
  element: HTMLElement,
  horizontalOffset = 0
): OverlayVirtualElement => ({
  contextElement: element,
  getBoundingClientRect: () => {
    const rect = getBlockedOnTrackStatusTextElement(element).getBoundingClientRect();
    const left = rect.left + horizontalOffset;
    const width = 1;

    return {
      x: left,
      y: rect.top,
      width,
      height: rect.height,
      top: rect.top,
      right: left + width,
      bottom: rect.bottom,
      left,
      toJSON: () => undefined,
    } as DOMRect;
  },
});

const BlockedOnTrackStatusTooltip = ({
  healthScoreCounts,
  isDisabled,
  children,
  triggerRef,
}: {
  healthScoreCounts: HealthScoreCounts | null;
  isDisabled: boolean;
  children?: React.ReactElement<TooltipTriggerProps>;
  triggerRef?: OverlayAnchorRef;
}) => {
  const childTriggerRef = useRef<HTMLElement | null>(null);
  const [isChildTooltipOpen, setIsChildTooltipOpen] = useState(false);

  if (!healthScoreCounts) return children;

  const title = (
    <Box style={{whiteSpace: 'pre-line'}}>
      {t('workstream_update_on_track_readiness_tooltip', {
        completedCount: healthScoreCounts.completedCount,
        totalCount: healthScoreCounts.totalCount,
      }).toString()}
    </Box>
  );
  if (triggerRef) {
    return (
      <Tooltip.Control
        open
        triggerRef={triggerRef}
        placement="top-start"
        maxWidth={600}
        shouldDisablePointerEvents
      >
        {title}
      </Tooltip.Control>
    );
  }

  return (
    children && (
      <>
        <Box
          onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
            childTriggerRef.current = getBlockedOnTrackStatusTextElement(e.currentTarget);
            setIsChildTooltipOpen(true);
          }}
          onMouseLeave={() => {
            childTriggerRef.current = null;
            setIsChildTooltipOpen(false);
          }}
          onFocus={(e: React.FocusEvent<HTMLElement>) => {
            childTriggerRef.current = getBlockedOnTrackStatusTextElement(e.currentTarget);
            setIsChildTooltipOpen(true);
          }}
          onBlur={() => {
            childTriggerRef.current = null;
            setIsChildTooltipOpen(false);
          }}
        >
          {children}
        </Box>
        <Tooltip.Control
          open={!isDisabled && isChildTooltipOpen}
          triggerRef={childTriggerRef}
          placement="top-start"
          maxWidth={600}
          shouldDisablePointerEvents
          offsetDistance={8}
        >
          {title}
        </Tooltip.Control>
      </>
    )
  );
};

const WorkstreamUpdateContent = ({
  workstream,
  setEditorValue,
  editorValue,
  status,
  setStatus,
  onCoachContextChange,
  onChatPersistenceFingerprintChange,
  onAgentInteractionChange,
  onAutodraftHydratedChange,
  onAutodraftEditedChange,
  onUserEdit,
  onPaste,
  onKeystroke,
  isOnTrackBlockedByReadiness,
  metrics,
  actualPeriod,
  onMetricChange,
  isUpdatingMetrics,
  activeStep,
  showTouchCurrentUpdatedAtControls,
  touchCurrentUpdatedAtMetricIds,
  onDraftCaptureChange,
  hasDraftMetricsBlockingUpdate,
}: {
  workstream: Workstream;
  setEditorValue: (editorValue: string) => void;
  editorValue?: string;
  status: string;
  setStatus: (status: string) => void;
  onCoachContextChange?: (ctx: CoachSubmissionContext | null) => void;
  onChatPersistenceFingerprintChange?: (fingerprint: string) => void;
  onAgentInteractionChange?: (used: boolean) => void;
  onAutodraftHydratedChange?: (hydrated: boolean) => void;
  onAutodraftEditedChange?: (edited: boolean) => void;
  /** Called just before a user-made change to the draft or status. */
  onUserEdit?: () => void;
  onPaste?: () => void;
  onKeystroke?: () => void;
  isOnTrackBlockedByReadiness: boolean;
  metrics: WorkstreamMetric[];
  actualPeriod?: string;
  onMetricChange: (
    metricId: number,
    update: WorkstreamMetricUpdate
  ) => Promise<WorkstreamMetric | void>;
  isUpdatingMetrics: boolean;
  activeStep: WorkstreamUpdateStep;
  showTouchCurrentUpdatedAtControls: boolean;
  touchCurrentUpdatedAtMetricIds?: ReadonlySet<number>;
  onDraftCaptureChange: (capture: (() => void) | null) => void;
  hasDraftMetricsBlockingUpdate: boolean;
}) => {
  const workstreamId = workstream.id;
  const {healthScore} = useWorkstreamHealthScore({
    workstreamId,
    checksType: 'default',
  });
  const healthScoreCounts = useWorkstreamHealthScoreCounts(workstream);
  const shouldShowOnTrackReadinessTooltip = isOnTrackBlockedByReadiness && status === 'on_track';
  const shouldBlockedOnTrackStatusOptionRef = useRef<OverlayVirtualElement | null>(null);
  const [shouldBlockedOnTrackStatusOption, setShouldBlockedOnTrackStatusOption] = useState(false);
  const {data: autodraft} = useWorkstreamAutodraft(workstreamId);
  const [hydratedFromAutodraft, setHydratedFromAutodraft] = useState(false);
  const autodraftEditTracked = useRef(false);

  const editorUpdateRef = useRef<{
    clearEditor: (update?: string) => void;
    getEditorState: () => EditorState;
    setContentWithDetectedFormat?: (content: string, options?: {selectAll?: boolean}) => void;
  }>(null);

  const editorLinkRef = useRef<{
    clearEditor: (resetValue?: string) => void;
    getEditorState: () => EditorState;
  }>(null);

  const statusSelectOptions = getStatusOptions();
  const statusSelectChildren = useMemo(
    () =>
      statusSelectOptions.map((statusOption) => (
        <Select.OptGroup key={statusOption.label} withLabel={t(statusOption.label)}>
          {statusOption.statusOptions.map(({value: optionValue}) => {
            const shouldDisableOption = isOnTrackBlockedByReadiness && optionValue === 'on_track';
            const shouldShowDisabledTooltip = shouldDisableOption && healthScoreCounts;
            return (
              <Select.Option
                aria-disabled={shouldDisableOption}
                className={styles.statusOptionOverride}
                disabled={shouldDisableOption}
                key={optionValue}
                onMouseEnter={
                  shouldShowDisabledTooltip
                    ? (e) => {
                        shouldBlockedOnTrackStatusOptionRef.current =
                          getBlockedOnTrackStatusTextAnchor(e.currentTarget, 28);
                        setShouldBlockedOnTrackStatusOption(true);
                      }
                    : undefined
                }
                onMouseLeave={
                  shouldShowDisabledTooltip
                    ? () => {
                        shouldBlockedOnTrackStatusOptionRef.current = null;
                        setShouldBlockedOnTrackStatusOption(false);
                      }
                    : undefined
                }
                value={optionValue}
                withAccessory={
                  shouldShowDisabledTooltip ? (
                    <BlockedOnTrackStatusWarning />
                  ) : (
                    <StatusButtonIcon
                      status={getWorkstreamUpdateVisualStatus(optionValue)}
                      includeArchived
                    />
                  )
                }
              >
                {t(optionValue).toString()}
              </Select.Option>
            );
          })}
        </Select.OptGroup>
      )),
    [healthScoreCounts, isOnTrackBlockedByReadiness, statusSelectOptions]
  );

  const syncEditorValueFromRefs = useCallback(
    (statusOverride?: string) => {
      setEditorValue(
        combineEditorStates(
          statusOverride ?? status,
          editorUpdateRef.current?.getEditorState(),
          editorLinkRef.current?.getEditorState()
        )
      );
    },
    [status, setEditorValue]
  );

  const updateEditorStates = useCallback(() => {
    syncEditorValueFromRefs();
    if (hydratedFromAutodraft && !autodraftEditTracked.current) {
      autodraftEditTracked.current = true;
      onAutodraftEditedChange?.(true);
      analyticsLogger().logEvent('AUTODRAFT_DRAFT_EDITED', {workstream_id: String(workstreamId)});
    }
  }, [syncEditorValueFromRefs, hydratedFromAutodraft, onAutodraftEditedChange, workstreamId]);

  useEffect(() => {
    onDraftCaptureChange(syncEditorValueFromRefs);
    return () => {
      onDraftCaptureChange(null);
    };
  }, [onDraftCaptureChange, syncEditorValueFromRefs]);

  const [chatDraftStacks, setChatDraftStacks] = useState<{
    undo: ChatDraftSnapshot[];
    redo: ChatDraftSnapshot[];
  }>({undo: [], redo: []});

  useEffect(() => {
    setChatDraftStacks({undo: [], redo: []});
  }, [workstream.id]);

  const captureChatDraftSnapshot = useCallback((): ChatDraftSnapshot => {
    const updateEditorJson = editorUpdateRef.current
      ? JSON.stringify(editorUpdateRef.current.getEditorState().toJSON())
      : '';
    let linkEditorJson: string | null = null;
    if (status === 'archived' && editorLinkRef.current) {
      linkEditorJson = JSON.stringify(editorLinkRef.current.getEditorState().toJSON());
    }
    return {
      status,
      updateEditorJson,
      linkEditorJson,
    };
  }, [status]);

  const pushEditorValueFromRefsForSnap = useCallback(
    (snap: ChatDraftSnapshot) => {
      setEditorValue(
        combineEditorStates(
          snap.status,
          editorUpdateRef.current?.getEditorState(),
          editorLinkRef.current?.getEditorState()
        )
      );
    },
    [setEditorValue]
  );

  const restoreChatDraftSnapshot = useCallback(
    (snap: ChatDraftSnapshot) => {
      flushSync(() => {
        setStatus(snap.status);
      });
      requestAnimationFrame(() => {
        try {
          const mainJson = snap.updateEditorJson.trim();
          editorUpdateRef.current?.clearEditor(
            mainJson && !serializedLexicalJsonIsVisuallyEmpty(mainJson) ? mainJson : undefined
          );
          if (snap.status === 'archived') {
            const l = snap.linkEditorJson?.trim() ?? '';
            editorLinkRef.current?.clearEditor(
              l && !serializedLexicalJsonIsVisuallyEmpty(l) ? l : undefined
            );
          }
        } finally {
          pushEditorValueFromRefsForSnap(snap);
          requestAnimationFrame(() => {
            pushEditorValueFromRefsForSnap(snap);
            requestAnimationFrame(() => {
              pushEditorValueFromRefsForSnap(snap);
            });
          });
        }
      });
    },
    [pushEditorValueFromRefsForSnap, setStatus]
  );

  const pushUndoBeforeChatApply = useCallback(() => {
    setChatDraftStacks((s) => ({
      undo: [...s.undo, captureChatDraftSnapshot()].slice(-MAX_CHAT_DRAFT_UNDO_STACK),
      redo: [],
    }));
  }, [captureChatDraftSnapshot]);

  const undoChatDraftApply = useCallback(() => {
    setChatDraftStacks((s) => {
      if (s.undo.length === 0) return s;
      const snapToRestore = s.undo[s.undo.length - 1];
      const current = captureChatDraftSnapshot();
      const next = {
        undo: s.undo.slice(0, -1),
        redo: [...s.redo, current].slice(-MAX_CHAT_DRAFT_UNDO_STACK),
      };
      requestAnimationFrame(() => restoreChatDraftSnapshot(snapToRestore));
      return next;
    });
  }, [captureChatDraftSnapshot, restoreChatDraftSnapshot]);

  const redoChatDraftApply = useCallback(() => {
    setChatDraftStacks((s) => {
      if (s.redo.length === 0) return s;
      const snapToRestore = s.redo[s.redo.length - 1];
      const current = captureChatDraftSnapshot();
      const next = {
        redo: s.redo.slice(0, -1),
        undo: [...s.undo, current].slice(-MAX_CHAT_DRAFT_UNDO_STACK),
      };
      requestAnimationFrame(() => restoreChatDraftSnapshot(snapToRestore));
      return next;
    });
  }, [captureChatDraftSnapshot, restoreChatDraftSnapshot]);

  const chatDraftHistoryControl = useMemo<ChatDraftHistoryControl>(
    () => ({
      canUndo: chatDraftStacks.undo.length > 0,
      canRedo: chatDraftStacks.redo.length > 0,
      onUndo: undoChatDraftApply,
      onRedo: redoChatDraftApply,
    }),
    [chatDraftStacks, undoChatDraftApply, redoChatDraftApply]
  );

  const onApplySuggestionToDraft = useCallback(
    (markdown: string, suggestedStatus?: string) => {
      // eslint-disable-next-line no-console
      console.log('[workstream-update] onApplySuggestionToDraft', {
        suggestedStatus,
        hasSetContentFn: Boolean(editorUpdateRef.current?.setContentWithDetectedFormat),
        markdown,
      });
      onUserEdit?.();
      pushUndoBeforeChatApply();
      editorUpdateRef.current?.setContentWithDetectedFormat?.(markdown, {selectAll: false});
      if (suggestedStatus != null) setStatus(suggestedStatus);
      const statusForCombine = suggestedStatus ?? status;
      // Push combined JSON to parent immediately so Assist sees the new draft (onChange may not
      // fire for imperative setContent). Re-sync on the next frame(s) for any late Lexical commits.
      syncEditorValueFromRefs(statusForCombine);
      requestAnimationFrame(() => {
        syncEditorValueFromRefs(statusForCombine);
        requestAnimationFrame(() => {
          syncEditorValueFromRefs(statusForCombine);
        });
      });
    },
    [onUserEdit, syncEditorValueFromRefs, setStatus, status, pushUndoBeforeChatApply]
  );

  // Prefill the editor once from a server-generated autodraft for the current
  // cycle (only when the editor is still empty so we never clobber user text).
  useEffect(() => {
    if (hydratedFromAutodraft) {
      return;
    }
    const markdown = autodraft?.generated_markdown;
    if (!markdown) {
      return;
    }
    let cancelled = false;
    let rafId = 0;
    // The Lexical editor registers its imperative handle asynchronously. If the autodraft resolves
    // from cache before the editor is ready, retry for a few frames instead of marking hydration
    // done — otherwise the generated draft is dropped silently and never re-applied.
    const applyWhenEditorReady = (attempt: number) => {
      if (cancelled) {
        return;
      }
      const editor = editorUpdateRef.current;
      if (!editor?.setContentWithDetectedFormat) {
        if (attempt < 30) {
          rafId = requestAnimationFrame(() => applyWhenEditorReady(attempt + 1));
        }
        return;
      }
      if (editorStateHasVisibleContent(editor.getEditorState())) {
        setHydratedFromAutodraft(true);
        return;
      }
      const suggestedStatus = autodraft?.generated_status ?? undefined;
      editor.setContentWithDetectedFormat(markdown, {selectAll: false});
      if (suggestedStatus) {
        setStatus(suggestedStatus);
      }
      syncEditorValueFromRefs(suggestedStatus ?? status);
      requestAnimationFrame(() => {
        if (!cancelled) {
          syncEditorValueFromRefs(suggestedStatus ?? status);
        }
      });
      setHydratedFromAutodraft(true);
      onAutodraftHydratedChange?.(true);
      analyticsLogger().logEvent('AUTODRAFT_DRAFT_VIEWED', {
        workstream_id: String(workstreamId),
        source: 'prefill',
      });
    };
    applyWhenEditorReady(0);
    return () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    autodraft,
    hydratedFromAutodraft,
    onAutodraftHydratedChange,
    setStatus,
    status,
    syncEditorValueFromRefs,
    workstreamId,
  ]);

  return (
    <Box display="flex" flexDirection="row" height="100%" width="100%">
      <Box
        display="flex"
        flexDirection="column"
        paddingRight="24"
        paddingTop="24"
        width="100%"
        style={{height: 'calc(100% - 24px)', overflow: 'auto', boxSizing: 'border-box'}}
      >
        <Box display="flex" flexDirection="row" marginBottom="20" style={{gap: 16}}>
          <Box style={{flex: '1 1 0', minWidth: 0}}>
            <FormRow>
              <FormLabel htmlFor="name">{t('status')}</FormLabel>
              <BlockedOnTrackStatusTooltip
                healthScoreCounts={healthScoreCounts}
                isDisabled={
                  !shouldShowOnTrackReadinessTooltip ||
                  !healthScoreCounts ||
                  Boolean(shouldBlockedOnTrackStatusOption)
                }
              >
                <Select
                  className={styles.statusSelect}
                  id="status"
                  onChange={(nextStatus: string) => {
                    onUserEdit?.();
                    setStatus(nextStatus);
                  }}
                  shouldShowOptionAccessory={false}
                  value={status}
                  withLeftAccessory={
                    shouldShowOnTrackReadinessTooltip && healthScoreCounts ? (
                      <BlockedOnTrackStatusWarning />
                    ) : (
                      <StatusButtonIcon
                        status={getWorkstreamUpdateVisualStatus(status)}
                        includeArchived
                      />
                    )
                  }
                  onClick={(e: React.SyntheticEvent) => {
                    e.stopPropagation();
                  }}
                >
                  {statusSelectChildren}
                </Select>
              </BlockedOnTrackStatusTooltip>
              {healthScoreCounts && shouldBlockedOnTrackStatusOption && (
                <BlockedOnTrackStatusTooltip
                  healthScoreCounts={healthScoreCounts}
                  isDisabled={false}
                  triggerRef={shouldBlockedOnTrackStatusOptionRef}
                />
              )}
            </FormRow>
          </Box>
          {healthScore && (
            <Box style={{flex: '1 1 0', minWidth: 0}}>
              <FormLabel>{t('health_readiness')}</FormLabel>
              <Box
                display="flex"
                alignItems="center"
                borderColor="Border Subtle"
                borderRadius="Medium"
                borderStyle="Solid"
                borderWidth="1"
                padding="12"
                style={{height: 56}}
              >
                <WorkstreamHealthScore
                  workstream={workstream}
                  iconSize={24}
                  source="workstream_update_status_modal"
                  showText
                  textSize="large"
                />
              </Box>
            </Box>
          )}
        </Box>
        {activeStep === 'metrics' && (
          <Box marginBottom="28">
            <Box marginBottom="12">
              <FormLabel>{t('metrics')}</FormLabel>
            </Box>
            {hasDraftMetricsBlockingUpdate && (
              <Box marginBottom="12">
                <Text color="error" size="small">
                  {t('workstream_update_metrics_need_status').toString()}
                </Text>
              </Box>
            )}
            <Box
              borderColor="Border Subtle"
              borderRadius="Medium"
              borderStyle="Solid"
              borderWidth="1"
              overflow="hidden"
              paddingX="20"
              paddingY="16"
            >
              <RedesignMetricsList
                workstreamId={workstream.id}
                workstreamName={workstream.name}
                metrics={metrics.filter(isPromptableUpdateMetric)}
                allEditableMetrics={metrics}
                selectedPeriod={`${CURRENT_YEAR}Q${CURRENT_QUARTER}`}
                selectedForecastType="target"
                handleMetricChange={onMetricChange}
                isPending={isUpdatingMetrics}
                disableCreateMetric
                showAllMetricsByDefault
                hideForecastColumn
                actualPeriod={actualPeriod}
                showTouchCurrentUpdatedAtControls={showTouchCurrentUpdatedAtControls}
                touchCurrentUpdatedAtMetricIds={touchCurrentUpdatedAtMetricIds}
                variant="table-in-update-modal"
              />
            </Box>
          </Box>
        )}
        {activeStep === 'milestones' && (
          <Box marginBottom="28">
            <Box marginBottom="12">
              <FormLabel>{t('milestones')}</FormLabel>
            </Box>
            <Box
              borderColor="Border Subtle"
              borderRadius="Medium"
              borderStyle="Solid"
              borderWidth="1"
              overflow="hidden"
              paddingX="20"
              paddingY="16"
            >
              <RedesignOutcomesList
                workstreamId={workstream.id}
                workstreamName={workstream.name}
                outcomes={metrics.filter(isUpdatableMilestone)}
                allEditableMetrics={metrics}
                owner={getWorkstreamDri(workstream)}
                selectedFilters={['milestone']}
                handleMetricChange={onMetricChange}
                isPending={isUpdatingMetrics}
                disableCreateMetric
                showAllOutcomesByDefault
                variant="table-in-update-modal"
              />
            </Box>
          </Box>
        )}
        <Box
          display={activeStep === 'status' ? 'flex' : 'none'}
          flexDirection="column"
          flexGrow={1}
        >
          {status === 'archived' && (
            <FormRow>
              <FormLabel>{t('relevant_workstream')}</FormLabel>
              <Box paddingBottom="8">
                <Text color="faint">{t('relevant_workstream_subtitle')}</Text>
              </Box>
              <Box flexGrow={1}>
                <RichTextArea
                  ref={editorLinkRef}
                  editable
                  disableTables={true}
                  minHeight={50}
                  source="workstream-status-link"
                  placeholder={t('relevant_workstream_placeholder')}
                  selectAll
                  onChange={updateEditorStates}
                />
              </Box>
            </FormRow>
          )}

          <Box display="flex" alignItems="center" style={{gap: 8}}>
            <FormLabel>{t('update')}</FormLabel>
            {hydratedFromAutodraft && (
              <Box
                borderColor="Border Subtle"
                borderRadius="Small"
                borderStyle="Solid"
                borderWidth="1"
                paddingX="6"
                marginBottom="8"
              >
                <Text size="small" color="subtle" isBold>
                  {t('autodraft_draft_badge')}
                </Text>
              </Box>
            )}
          </Box>
          <Box flexGrow={1}>
            <RichTextArea
              key={workstream.id}
              ref={editorUpdateRef}
              editable
              disableTables={true}
              minHeight="100%"
              topOffset={-25}
              source="workstream-status-update"
              pasteDiagnostics
              trackPaste={true}
              onPaste={onPaste}
              onKeystroke={onKeystroke}
              placeholder={t('start_writing_placeholder')}
              selectAll
              stripUnknownCommentThreads
              onChange={updateEditorStates}
            />
          </Box>
        </Box>
      </Box>

      <Box
        borderLeft="Solid"
        borderWidth="1"
        borderColor="Border Subtle"
        display="flex"
        flexDirection="column"
        style={{
          height: '100%',
          minHeight: 0,
          overflowY: 'hidden',
          flex: '0 0 40%',
        }}
      >
        <WorkstreamUpdateTabs
          workstream={workstream}
          editorValue={editorValue}
          selectedStatus={status}
          onCoachContextChange={onCoachContextChange}
          onApplySuggestionToDraft={onApplySuggestionToDraft}
          onChatPersistenceFingerprintChange={onChatPersistenceFingerprintChange}
          onAgentInteractionChange={onAgentInteractionChange}
          chatDraftHistoryControl={chatDraftHistoryControl}
        />
      </Box>
    </Box>
  );
};

export interface WorkstreamUpdateRef {
  confirmUnsavedChanges: () => boolean;
  isSubmissionInProgress: () => boolean;
}

export interface WorkstreamUpdateSubmitResult {
  status: string;
  created_at: string;
}

export const WorkstreamUpdate = forwardRef<
  WorkstreamUpdateRef,
  {
    source: string;
    workstream: Workstream;
    onAfterSubmit: (result?: WorkstreamUpdateSubmitResult) => void | Promise<void>;
    statusOverride?: string;
  }
>(({source, workstream, onAfterSubmit, statusOverride}, ref) => {
  const editorRef = useRef<{clearEditor: () => void}>(null);
  const editorValueRef = useRef<string>();
  const draftCaptureRef = useRef<(() => void) | null>(null);

  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const {isAskWorkstreamUpdateEnabled} = useAtomValue(growthbookCacheAtom);
  const agentType = isAskWorkstreamUpdateEnabled ? 'ask' : 'dash';
  const {latestUpdate} = useWorkstreamDisplayStatus(workstream);
  const {createUpdate, isPending} = useWorkstreamStatus();
  const {updateMetric, isPending: isUpdatingMetrics} = useWorkstreamMetricMutation();
  const {data: draftPolicy} = useWorkstreamDraftPolicy();
  const auditOnTimeTimeframe = useDefaultWorkstreamTimeframe();
  const [editorValue, setEditorValueState] = useState<string>();
  const [metrics, setMetrics] = useState<WorkstreamMetric[]>(workstream.metrics);
  const originalMetricsRef = useRef<WorkstreamMetric[]>(workstream.metrics);
  const [actualPeriod, setActualPeriod] = useState<string | undefined>(() =>
    workstream.metrics.some(needsPreviousQuarterActual) ? previousQuarterPeriod : undefined
  );
  const [metricUpdateFields, setMetricUpdateFields] = useState<Map<number, WorkstreamMetricUpdate>>(
    new Map()
  );
  const [activeStep, setActiveStep] = useState<WorkstreamUpdateStep>('status');
  const previousWorkstreamIdRef = useRef(workstream.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmissionHandoffInProgress, setIsSubmissionHandoffInProgress] = useState(false);
  const [coachSubmissionContext, setCoachSubmissionContext] =
    useState<CoachSubmissionContext | null>(null);
  const [status, setStatus] = useState(statusOverride ?? latestUpdate?.status ?? 'no_status');
  // The form as it stood just before the user's first edit. Content that loads in on its own
  // after open (autodraft prefill, the chat panel mounting) doesn't count as unsaved changes.
  const [editBaseline, setEditBaseline] = useState<{editorValue: string; status: string} | null>(
    null
  );
  const statusRef = useRef(status);
  statusRef.current = status;
  const chatBaselineRef = useRef<string | null>(null);
  const [hasChatChanges, setHasChatChanges] = useState(false);
  const [agentInteracted, setAgentInteracted] = useState<string | null>(null);
  const [autodraftHydrated, setAutodraftHydrated] = useState(false);
  const [autodraftEdited, setAutodraftEdited] = useState(false);
  const [hasPasted, setHasPasted] = useState(false);
  const keystrokeCountRef = useRef(0);
  const isOnTrackBlockedByReadiness = useBlockOnTrackForReadiness(workstream.id);
  const {rawHealthScore} = useWorkstreamHealthScore({
    workstreamId: workstream.id,
    checksType: 'default',
  });
  const showTouchCurrentUpdatedAtControls = isTop50OrSubWorkstream(
    rawHealthScore?.workstream_pillar_metadata?.pillar_level
  );

  const setEditorValue = useCallback((value: string) => {
    editorValueRef.current = value;
    setEditorValueState(value);
  }, []);

  const setDraftCapture = useCallback((capture: (() => void) | null) => {
    draftCaptureRef.current = capture;
  }, []);

  const markUserEdit = useCallback(() => {
    const snapshot = {editorValue: editorValueRef.current ?? '', status: statusRef.current};
    setEditBaseline((current) => current ?? snapshot);
  }, []);

  const handleChatFingerprintChange = useCallback((fingerprint: string) => {
    chatBaselineRef.current ??= fingerprint;
    setHasChatChanges(fingerprint !== chatBaselineRef.current);
  }, []);

  useEffect(() => {
    if (previousWorkstreamIdRef.current === workstream.id) {
      return;
    }

    previousWorkstreamIdRef.current = workstream.id;
    originalMetricsRef.current = workstream.metrics;
    setActualPeriod(
      workstream.metrics.some(needsPreviousQuarterActual) ? previousQuarterPeriod : undefined
    );
    setMetrics(workstream.metrics);
    setMetricUpdateFields(new Map());
    setActiveStep('status');
    setEditBaseline(null);
    chatBaselineRef.current = null;
    setHasChatChanges(false);
  }, [workstream.id, workstream.metrics]);

  const stagedMetricUpdates = useMemo(
    () =>
      [...metricUpdateFields.entries()].flatMap(([metricId, fields]) => {
        const metric = metrics.find(({id}) => id === metricId);
        if (!metric) return [];
        const originalMetric = originalMetricsRef.current.find(({id}) => id === metric.id);
        if (!originalMetric) return [];

        const update = Object.fromEntries(
          Object.entries(fields).filter(([key, value]) =>
            key === 'touch_current_updated_at'
              ? value === true
              : value !== originalMetric[key as keyof WorkstreamMetric]
          )
        ) as WorkstreamMetricUpdate;

        return Object.keys(update).length > 0 ? [{metric, originalMetric, update}] : [];
      }),
    [metricUpdateFields, metrics]
  );

  const touchCurrentUpdatedAtMetricIds = useMemo(
    () =>
      new Set(
        [...metricUpdateFields.entries()]
          .filter(([, fields]) => fields.touch_current_updated_at === true)
          .map(([metricId]) => metricId)
      ),
    [metricUpdateFields]
  );

  const handleStagedMetricChange = useCallback(
    (metricId: number, update: WorkstreamMetricUpdate): Promise<WorkstreamMetric | void> => {
      const originalMetric = originalMetricsRef.current.find(({id}) => id === metricId);
      const nextUpdate =
        update.touch_current_updated_at === true && originalMetric
          ? {...update, current: originalMetric.current}
          : update;
      const visibleUpdate = {...nextUpdate};
      delete visibleUpdate.touch_current_updated_at;
      let updatedMetric: WorkstreamMetric | undefined;
      setMetrics((currentMetrics) =>
        currentMetrics.map((metric) => {
          if (metric.id !== metricId) return metric;
          updatedMetric = {...metric, ...visibleUpdate};
          return updatedMetric;
        })
      );
      setMetricUpdateFields((current) => {
        const next = new Map(current);
        next.set(metricId, {...next.get(metricId), ...nextUpdate});
        return next;
      });
      return Promise.resolve(updatedMetric);
    },
    []
  );

  // Update status when workstream changes
  useEffect(() => {
    if (statusOverride) {
      setStatus(statusOverride);
    } else {
      setStatus(latestUpdate?.status ?? 'no_status');
    }
  }, [latestUpdate, statusOverride]);

  useEffect(() => {
    editorRef.current?.clearEditor();
    setAgentInteracted(null);
    setHasPasted(false);
    keystrokeCountRef.current = 0;
  }, [workstream]);

  const canSave = Boolean(editorValue?.toString());
  const isSaving = isSubmitting || isSubmissionHandoffInProgress || isPending || isUpdatingMetrics;
  const updateSteps = useMemo(() => getWorkstreamUpdateSteps(metrics), [metrics]);
  const isLastStep = activeStep === updateSteps.at(-1);
  const shouldDisableNextFromReadiness = isOnTrackBlockedByReadiness && status === 'on_track';
  const hasDraftMetricStatus = useMemo(
    () =>
      metrics.some(
        (metric) =>
          isPromptableUpdateMetric(metric) &&
          (metric.status == null || metric.status === 'no_status')
      ),
    [metrics]
  );
  const shouldCheckAuditForDraftMetricStatus =
    hasDraftMetricStatus && !isWorkstreamPlanningWindowActive(draftPolicy);
  const {
    data: workstreamAudit,
    isError: isWorkstreamAuditError,
    isFetching: isWorkstreamAuditFetching,
  } = useWorkstreamAudit({
    // Until the timeframes load, the audit stays pending (no audit yet).
    enabled: shouldCheckAuditForDraftMetricStatus && Boolean(auditOnTimeTimeframe),
    onTimeTimeframe: auditOnTimeTimeframe,
  });
  const isWorkstreamInAudit = useMemo(
    () => workstreamAuditIncludesWorkstream(workstreamAudit, workstream.id),
    [workstream.id, workstreamAudit]
  );
  const hasDraftMetricsBlockingUpdate = shouldCheckAuditForDraftMetricStatus && isWorkstreamInAudit;
  const isWorkstreamAuditMembershipPending =
    shouldCheckAuditForDraftMetricStatus &&
    (isWorkstreamAuditFetching || isWorkstreamAuditError || !workstreamAudit);
  const shouldDisableProgressFromDraftMetrics =
    activeStep === 'metrics' &&
    (hasDraftMetricsBlockingUpdate || isWorkstreamAuditMembershipPending);

  useEffect(() => {
    if (!updateSteps.includes(activeStep)) {
      setActiveStep('status');
    }
  }, [activeStep, updateSteps]);

  const hasUnsavedChanges =
    stagedMetricUpdates.length > 0 ||
    hasChatChanges ||
    (editBaseline != null &&
      ((editorValue ?? '') !== editBaseline.editorValue || status !== editBaseline.status));
  const {confirmUnsavedChanges: confirmDiscardUnsavedChanges} = useConfirmUnsavedChanges([], {
    isDirty: hasUnsavedChanges,
  });
  const confirmUnsavedChanges = useCallback(
    () => !isSaving && confirmDiscardUnsavedChanges(),
    [confirmDiscardUnsavedChanges, isSaving]
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    draftCaptureRef.current?.();
    if (
      !editorValueRef.current ||
      hasDraftMetricsBlockingUpdate ||
      isWorkstreamAuditMembershipPending
    )
      return;
    setIsSubmitting(true);
    // Count unique records changed as part of this save, rather than every
    // metric/outcome currently attached to the workstream.  This keeps the
    // event aligned with what the user actually submitted.
    const metricUpdatesCount = stagedMetricUpdates.filter(
      ({metric}) => metric.type === 'metric'
    ).length;
    // Product reporting treats both outcome types as milestones here.
    const milestoneUpdatesCount = stagedMetricUpdates.filter(
      ({metric}) => metric.type === 'milestone' || metric.type === 'deliverable'
    ).length;
    const createdAt = new Date().toISOString();
    const ctx = coachSubmissionContext as
      | {must_address?: unknown[]; feedback?: unknown[]; coach_mode?: string | null}
      | null
      | undefined;
    const hasCoachContext =
      ctx &&
      (ctx.coach_mode === 'assist' ||
        Boolean(ctx.must_address?.length) ||
        Boolean(ctx.feedback?.length));
    try {
      await Promise.all([
        createUpdate({
          ownerId: employee.user_id,
          update: {
            comment: editorValueRef.current ?? editorValue?.toString() ?? '',
            status,
            workstream_id: workstream.id,
            created_at: createdAt,
            ...(hasCoachContext && {
              coach_submission_context: coachSubmissionContext as Parameters<
                typeof createUpdate
              >[0]['update']['coach_submission_context'],
            }),
          },
        }),
        ...stagedMetricUpdates.map(({metric, originalMetric, update}) =>
          updateMetric(workstream.id, metric.id, originalMetric, update)
        ),
      ]);

      if (statusOverride === 'archived') {
        analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
          source: 'workstream_details',
          workstream_id: workstream.id,
          property: 'lifecycle',
          type: 'delete',
        });
      }

      setIsSubmissionHandoffInProgress(true);
      await onAfterSubmit({status, created_at: createdAt});

      analyticsLogger().logEvent('WORKSTREAM_STATUS_UPDATE', {
        source,
        status,
        user: getUserType(workstream, employee),
        agent_used: !!agentInteracted,
        agent_type: agentInteracted ?? undefined,
        metric_updates_count: metricUpdatesCount,
        milestone_updates_count: milestoneUpdatesCount,
        has_metric_updates: metricUpdatesCount > 0,
        has_milestone_updates: milestoneUpdatesCount > 0,
        workstream_id: workstream.id,
        channel: 'web',
        pillar_level: rawHealthScore?.workstream_pillar_metadata?.pillar_level,
        pillar_workstream_name: rawHealthScore?.workstream_pillar_metadata?.pillar_workstream_name,
        pasted: hasPasted,
        total_keystrokes: keystrokeCountRef.current,
        autodraft_used: autodraftHydrated,
        autodraft_edited: autodraftEdited,
      });

      if (autodraftHydrated) {
        analyticsLogger().logEvent('AUTODRAFT_DRAFT_ACCEPTED', {
          workstream_id: String(workstream.id),
          status,
          edited: autodraftEdited ? 'true' : 'false',
          source: agentInteracted ?? 'dash',
        });
      }

      trackSprigEvent('WorkstreamStatusUpdateSubmitted');
    } catch (error) {
      setIsSubmissionHandoffInProgress(false);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    agentInteracted,
    autodraftEdited,
    autodraftHydrated,
    coachSubmissionContext,
    createUpdate,
    editorValue,
    employee,
    hasDraftMetricsBlockingUpdate,
    hasPasted,
    isSubmitting,
    isWorkstreamAuditMembershipPending,
    onAfterSubmit,
    rawHealthScore,
    source,
    stagedMetricUpdates,
    status,
    statusOverride,
    updateMetric,
    workstream,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        e.stopPropagation();
        if (confirmUnsavedChanges()) {
          onAfterSubmit();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (isLastStep) {
          void handleSubmit();
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [confirmUnsavedChanges, handleSubmit, isLastStep, onAfterSubmit]);

  useImperativeHandle(ref, () => ({
    confirmUnsavedChanges,
    isSubmissionInProgress: () => isSaving,
  }));

  return (
    <>
      <Modal.Header>
        <Box display="flex" flexDirection="row" style={{gap: 16}}>
          <Stack gap="6">
            <LabelGroup
              verticalAlignment="center"
              withLabel={<Eyebrow>{t('workstream')}</Eyebrow>}
            />
            <Title size={18}>{workstream.name}</Title>
          </Stack>
        </Box>
      </Modal.Header>

      <Modal.Body
        className="no-padding"
        style={{height: 'calc(100vh - 200px)', overflow: 'hidden', paddingRight: 0}}
      >
        <WorkstreamUpdateContent
          workstream={workstream}
          setEditorValue={setEditorValue}
          editorValue={editorValue}
          status={status}
          setStatus={setStatus}
          onCoachContextChange={setCoachSubmissionContext}
          onChatPersistenceFingerprintChange={handleChatFingerprintChange}
          onAgentInteractionChange={() => setAgentInteracted((prev) => prev ?? agentType)}
          onAutodraftHydratedChange={setAutodraftHydrated}
          onAutodraftEditedChange={setAutodraftEdited}
          onUserEdit={markUserEdit}
          onPaste={() => {
            markUserEdit();
            setHasPasted(true);
          }}
          onKeystroke={() => {
            markUserEdit();
            keystrokeCountRef.current += 1;
          }}
          isOnTrackBlockedByReadiness={isOnTrackBlockedByReadiness}
          metrics={metrics}
          actualPeriod={actualPeriod}
          onMetricChange={handleStagedMetricChange}
          isUpdatingMetrics={isUpdatingMetrics}
          activeStep={activeStep}
          showTouchCurrentUpdatedAtControls={showTouchCurrentUpdatedAtControls}
          touchCurrentUpdatedAtMetricIds={touchCurrentUpdatedAtMetricIds}
          onDraftCaptureChange={setDraftCapture}
          hasDraftMetricsBlockingUpdate={hasDraftMetricsBlockingUpdate}
        />
      </Modal.Body>
      <Modal.Footer hasTopBorder>
        <Box display="flex" alignItems="center" width="100%" style={{position: 'relative'}}>
          {updateSteps.length > 1 && (
            <Box
              data-testid="workstream-update-progress"
              display="flex"
              style={{gap: 4, left: '50%', position: 'absolute', transform: 'translateX(-50%)'}}
            >
              {updateSteps.map((step) => (
                <Box
                  key={step}
                  borderRadius="Circular"
                  backgroundColor={activeStep === step ? 'Primary Base' : 'Background Subtle'}
                  style={{width: 10, height: 10}}
                />
              ))}
            </Box>
          )}
          <Box display="flex" marginLeft="auto" style={{gap: 12}}>
            {activeStep !== 'status' && (
              <Button
                variant="outline"
                disabled={isSaving}
                onClick={() => setActiveStep(updateSteps[updateSteps.indexOf(activeStep) - 1])}
              >
                {t('back')}
              </Button>
            )}
            {isLastStep ? (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSave || isSaving || shouldDisableProgressFromDraftMetrics}
              >
                <Box display="flex" alignItems="center" style={{gap: 6}}>
                  {isSaving && <Spinner size="small" monochromatic />}
                  {t('update')}
                </Box>
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  draftCaptureRef.current?.();
                  if (!editorValueRef.current) return;
                  setActiveStep(updateSteps[updateSteps.indexOf(activeStep) + 1]);
                }}
                disabled={
                  (activeStep === 'status' && (!canSave || shouldDisableNextFromReadiness)) ||
                  shouldDisableProgressFromDraftMetrics
                }
              >
                {t('next')}
              </Button>
            )}
          </Box>
        </Box>
      </Modal.Footer>
    </>
  );
});

WorkstreamUpdate.displayName = 'WorkstreamUpdate';
